import { Search, X } from 'lucide-react';
import {
    reasonLabel,
    type CreditNoteFilters, type CreditNoteReasonCatalogEntry, type CreditNoteSummary,
    type CreditNoteVocabularies,
} from '../../types/creditNotes';
import { formatCount, monthsAgoIso, tomorrowIso } from '../../utils/creditNoteFormat';

/**
 * The filter bar.
 *
 * THE DROPDOWNS ARE BUILT FROM THE UNNARROWED WINDOW. `summary.byCompany` and `summary.byReason`
 * are computed server-side over every document in the period, before any filter runs — so
 * picking dbi does not make the other seven companies disappear from the list and strand the
 * reader inside their own filter.
 *
 * THE REASON DROPDOWN FILTERS ON THE APP'S VOCABULARY, NOT ON THE ERP STRING (P4). Decision 2
 * gives the group ONE list of ten codes: where the ERP carries `GU_Category_BIG` it prefills
 * them, and where it does not — dch, dit and four more — a reviewer records one from the same
 * list. Filtering on the raw ERP string could only ever reach dbi and bin, which is precisely
 * the comparison this page exists to make possible. The endpoint still honours the old
 * `reasonCode` parameter, so a link built before P4 keeps working.
 *
 * THE LABELS ARE STILL THE ERP'S OWN 20-CHARACTER STUBS (`1 INTERNE GU`,
 * `8 H CLAIM SCHADENERS`) because `CreditNoteReasonCatalog.ReasonLabelDe` is NULL for all ten —
 * open question 1, with Stefan. Inventing labels here would put words in the business's mouth.
 *
 * SORTING IS NUMERIC ON THE LEADING TOKEN, and it comes from the catalogue's own `SortOrder`
 * rather than being redone here. Alphabetically, `10 ÜBRIGES` sorts between `1` and `2`, which
 * is how a ten-item list becomes unreadable.
 *
 * EVERY OPTION CARRIES ITS COUNT, AND THE COUNT IS THE SERVER'S. `summary.byAppReason`,
 * `byReviewStatus` and `byRecourseStatus` are computed over the same rows the filters run
 * against, so picking an option returns exactly the number printed beside it.
 */

const PERIODS: { label: string; months: number }[] = [
    { label: 'Last 12 months', months: 12 },
    { label: 'Last 24 months', months: 24 },
    { label: 'Last 6 months', months: 6 },
    { label: 'Last 3 months', months: 3 },
];

interface Props {
    filters: CreditNoteFilters;
    summary: CreditNoteSummary;
    availableCompanies: string[];
    /** Ordered by the catalogue's SortOrder — numeric on the leading token, from the server. */
    reasonCatalog: CreditNoteReasonCatalogEntry[];
    vocabularies: CreditNoteVocabularies;
    activeFilterCount: number;
    onChange: (next: Partial<CreditNoteFilters>) => void;
    onClear: () => void;
}

export function CreditNoteFiltersBar({
    filters, summary, availableCompanies, reasonCatalog, vocabularies,
    activeFilterCount, onChange, onClear,
}: Props) {
    const noReasonCount = summary.byAppReason?.NONE ?? 0;

    // Which preset the current window matches, so the select shows the truth after a deep link
    // rather than snapping to a period the list is not actually showing.
    const activePeriod = PERIODS.find(p => filters.dateFrom === monthsAgoIso(p.months))?.months ?? '';

    return (
        <div className="flex flex-wrap items-end gap-3">
            <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Search</span>
                <span className="relative inline-block">
                    <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                    <input
                        value={filters.search}
                        onChange={e => onChange({ search: e.target.value })}
                        placeholder="Customer, number, reason text"
                        className="h-9 w-60 rounded border border-gray-300 pl-7 pr-2 text-sm"
                    />
                </span>
            </label>

            <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Company</span>
                <select
                    value={filters.companyCode}
                    onChange={e => onChange({ companyCode: e.target.value })}
                    className="h-9 w-44 rounded border border-gray-300 px-2 text-sm"
                >
                    <option value="">All companies</option>
                    {availableCompanies.map(c => (
                        <option key={c} value={c}>
                            {c}{summary.byCompany[c] !== undefined ? ` (${formatCount(summary.byCompany[c])})` : ''}
                        </option>
                    ))}
                </select>
            </label>

            <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Reason</span>
                <select
                    value={filters.appReasonCode}
                    onChange={e => onChange({ appReasonCode: e.target.value })}
                    className="h-9 w-56 rounded border border-gray-300 px-2 text-sm"
                    title="The group's own ten codes. Prefilled from the ERP at dbi and bin, recorded in the app everywhere else - one vocabulary, so the companies stay comparable."
                >
                    <option value="">Any reason</option>
                    {reasonCatalog.map(c => (
                        <option key={c.reasonCode} value={c.reasonCode}>
                            {reasonLabel(c)} ({formatCount(summary.byAppReason?.[c.reasonCode] ?? 0)})
                        </option>
                    ))}
                    {/* Not a code, and not hidden either: this is the work still to be done. */}
                    <option value="NONE">Not recorded ({formatCount(noReasonCount)})</option>
                </select>
            </label>

            <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Booking review</span>
                <select
                    value={filters.reviewStatus}
                    onChange={e => onChange({ reviewStatus: e.target.value })}
                    className="h-9 w-40 rounded border border-gray-300 px-2 text-sm"
                >
                    <option value="">Any status</option>
                    {vocabularies.reviewStatuses.map(v => (
                        <option key={v.code} value={v.code}>
                            {v.label} ({formatCount(summary.byReviewStatus?.[v.code] ?? 0)})
                        </option>
                    ))}
                </select>
            </label>

            <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Recourse</span>
                <select
                    value={filters.recourseStatus}
                    onChange={e => onChange({ recourseStatus: e.target.value })}
                    className="h-9 w-40 rounded border border-gray-300 px-2 text-sm"
                >
                    <option value="">Any</option>
                    {/* "None" is a real state - a document nobody has opened a case on - and it
                        is the majority of the list. Folding it into "Open" would count untouched
                        documents as claims somebody is chasing. */}
                    <option value="NONE">None ({formatCount(summary.byRecourseStatus?.NONE ?? 0)})</option>
                    {vocabularies.recourseStatuses.map(v => (
                        <option key={v.code} value={v.code}>
                            {v.label} ({formatCount(summary.byRecourseStatus?.[v.code] ?? 0)})
                        </option>
                    ))}
                </select>
            </label>

            <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600">Period</span>
                <select
                    value={activePeriod}
                    onChange={e => {
                        const months = Number(e.target.value);
                        if (!Number.isFinite(months) || months === 0) return;
                        onChange({ dateFrom: monthsAgoIso(months), dateTo: tomorrowIso() });
                    }}
                    className="h-9 w-40 rounded border border-gray-300 px-2 text-sm"
                >
                    {activePeriod === '' && <option value="">Custom window</option>}
                    {PERIODS.map(p => <option key={p.months} value={p.months}>{p.label}</option>)}
                </select>
            </label>

            {activeFilterCount > 0 && (
                <button
                    onClick={onClear}
                    className="inline-flex h-9 items-center gap-1 rounded border border-gray-300 px-3 text-sm text-gray-600 hover:bg-gray-50"
                    title="Clears search, company, reason, both status filters and the rule tile. The period is left alone - changing it would change the population every tile counts over."
                >
                    <X className="h-4 w-4" /> Clear filters ({activeFilterCount})
                </button>
            )}
        </div>
    );
}
